import {Fragment} from 'react';
import {Listbox, Transition} from '@headlessui/react';
import cx from 'classnames';
import {Chevron} from 'Icons/Chevron';
import CheckMark from 'Icons/CheckMark';
import {slate} from 'tailwindcss/colors';

interface Props {
    items: string[];
    titleClasses?: string;
    buttonClassNames?: string;
    optionsClassNames?: string;
    title?: string;
    setSelected: (string) => void;
    selected: string;
}

export const Select: React.FC<Props> = ({
    items,
    titleClasses,
    title,
    setSelected,
    selected,
    buttonClassNames,
    optionsClassNames,
}) => {
    return (
        <Listbox value={selected} onChange={setSelected}>
            {({open}) => (
                <>
                    {title && (
                        <Listbox.Label
                            className={cx('block text-sm font-medium leading-6 text-gray-900', titleClasses)}
                        >
                            {title}
                        </Listbox.Label>
                    )}
                    <div className="relative">
                        <Listbox.Button
                            className={cx(
                                'relative flex w-full cursor-default flex-row items-center justify-start gap-1 rounded-2xl bg-zinc-200 px-2 py-3 text-left text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400 sm:text-sm sm:leading-6',
                                buttonClassNames
                            )}
                        >
                            <span className="flex items-center">
                                <span className="ml-0.5 mr-4 block truncate">{selected}</span>
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-1">
                                <Chevron className="" aria-hidden="true" color={slate['700']} />
                            </span>
                        </Listbox.Button>

                        <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options
                                className={cx(
                                    'absolute z-10 mt-1 max-h-56 min-w-full overflow-auto rounded-md bg-zinc-200 py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
                                    optionsClassNames
                                )}
                            >
                                {items.map((item) => (
                                    <Listbox.Option
                                        key={item}
                                        className={({active}) =>
                                            cx(
                                                'text-slate-700',
                                                active && 'bg-sky-400 bg-opacity-40',
                                                'relative cursor-default select-none px-3 py-2 text-left'
                                            )
                                        }
                                        value={item}
                                    >
                                        {({selected, active}) => (
                                            <>
                                                <div className="flex items-center">
                                                    <span
                                                        className={cx(
                                                            selected ? 'font-semibold' : 'font-normal',
                                                            'block truncate'
                                                        )}
                                                    >
                                                        {item}
                                                    </span>
                                                </div>

                                                {selected ? (
                                                    <span
                                                        className={cx(
                                                            active ? 'text-slate-700' : 'text-sky-500',
                                                            'absolute inset-y-0 right-0 flex items-center pr-2'
                                                        )}
                                                    >
                                                        <CheckMark className="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                ) : null}
                                            </>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                </>
            )}
        </Listbox>
    );
};
